/**
 * ReviewPage - 評価レビュー画面
 *
 * 上段: 指摘 ⇔ 文字起こしハイライト連動ビュー（frontend/ の ManagerReviewView から移植）
 *   - 主発言は常に赤でハイライト、関連発言（supporting）は選択中のカードに紐づくもののみ amber 表示
 *   - 選択中の発話は省略解除で全文表示
 *   - 指摘番号と文字起こし左の番号バッジが連動
 *   - section_display_name バッジは削除済み
 *
 * 下段: 既存のレビュー完了 / スキップフロー（レビュアー割り当て・コメント投稿・状態表示）
 *
 * バックエンド API:
 *   - GET /sessions/{id}                  : 既存（セッション概要 + reviewers）
 *   - GET /sessions/{id}/feedback         : 既存（AI findings）
 *   - GET /sessions/{id}/review           : 新規（ReviewSessionDetailResponse、B 系レーンで実装予定）
 *   - POST /sessions/{id}/review/complete : 既存
 *   - POST /sessions/{id}/review/skip     : 既存
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuthStore } from '@/state/authStore';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingState } from '@/components/Spinner';
import type { SessionStatus } from '@/types/session';
import { ReviewTranscriptPanel } from './ReviewTranscriptPanel';
import { useReviewSession } from './useReviewSession';

function SeverityBadge({ severity }: { severity: string }) {
  const c: Record<string, { label: string; cls: string }> = {
    critical: { label: '重大', cls: 'bg-red-100 text-red-800' },
    high: { label: '高', cls: 'bg-orange-100 text-orange-800' },
    medium: { label: '中', cls: 'bg-yellow-100 text-yellow-800' },
    low: { label: '低', cls: 'bg-blue-100 text-blue-800' },
  };
  const cfg = c[severity] ?? { label: severity, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>;
}

interface Finding {
  severity: string;
  title: string;
  body: string;
  section_index: number;
}
interface Reviewer {
  reviewer_user_id: string;
  reviewer_name: string;
  state: string;
}

export function ReviewPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.currentUser);

  const [session, setSession] = useState<Record<string, unknown> | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [reviewers, setReviewers] = useState<Reviewer[]>([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const {
    detail: reviewDetail,
    loading: reviewDetailLoading,
    error: reviewDetailError,
  } = useReviewSession(sessionId);

  useEffect(() => {
    if (!sessionId) return;
    Promise.all([
      api.get(`/sessions/${sessionId}`),
      api
        .get(`/sessions/${sessionId}/feedback`)
        .catch(() => ({ data: { ai_result: { findings: [] } } })),
    ])
      .then(([sessResp, fbResp]) => {
        setSession(sessResp.data);
        setReviewers(sessResp.data.reviewers ?? []);
        setFindings(fbResp.data.ai_result?.findings ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [sessionId]);

  const myReviewer = reviewers.find((r) => r.reviewer_user_id === String(user?.id));
  const canAct = myReviewer && ['pending', 'in_progress'].includes(myReviewer.state);
  const showSkip = canAct && reviewers.length >= 2;

  const handleComplete = async () => {
    if (!comment.trim()) {
      alert('コメントを入力してください');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/sessions/${sessionId}/review/complete`, { body: comment });
      navigate('/dashboard');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      alert(msg || 'エラーが発生しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (!confirm('このセッションのフィードバックをスキップしますか？')) return;
    setSubmitting(true);
    try {
      await api.post(`/sessions/${sessionId}/review/skip`);
      navigate('/dashboard');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      alert(msg || 'エラーが発生しました');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><LoadingState /></div>;
  if (!session)
    return <div className="p-8 text-center text-red-500">セッションが見つかりません</div>;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4">
        <Link to="/dashboard" className="text-sm text-blue-600 hover:underline">
          ← ダッシュボード
        </Link>
      </div>
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">レビュー</h1>
        <StatusBadge status={session.status as SessionStatus} />
      </div>
      <p className="mt-1 text-sm text-gray-500">
        {session.client_name as string} / {session.coach_name as string} —{' '}
        {session.day_type as string}
      </p>

      {/* 指摘 ⇔ 文字起こしハイライト連動ビュー */}
      <div className="mt-6 flex min-h-[32rem] flex-col">
        {reviewDetailLoading ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white p-12">
            <LoadingState message="レビュー詳細を読み込み中..." />
          </div>
        ) : reviewDetailError ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 p-8 text-sm text-amber-800">
            {reviewDetailError}
          </div>
        ) : reviewDetail ? (
          <ReviewTranscriptPanel detail={reviewDetail} />
        ) : (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white p-12 text-sm text-gray-400">
            レビュー詳細が取得できませんでした
          </div>
        )}
      </div>

      {/* 既存の AI 指摘 + フィードバック完了 / スキップフロー */}
      <div className="mt-6 grid grid-cols-2 gap-6">
        {/* AI 指摘（サマリー） */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">AI 指摘サマリー（{findings.length}件）</h3>
          {findings.map((f, i) => (
            <div key={i} className="rounded-lg bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <SeverityBadge severity={f.severity} />
                <span className="text-xs text-gray-400">セクション {f.section_index}</span>
              </div>
              <p className="mt-2 text-sm font-medium text-gray-900">{f.title}</p>
              <p className="mt-1 text-sm text-gray-600">{f.body}</p>
            </div>
          ))}
          {findings.length === 0 && (
            <p className="text-sm text-gray-400">AI 指摘はありません</p>
          )}
        </div>

        {/* コメント入力 + ボタン */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700">フィードバック</h3>

          {!canAct && myReviewer && (
            <div className="mt-3 rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
              このセッションのレビューは完了済みです（{myReviewer.state}）
            </div>
          )}

          {!myReviewer && (
            <div className="mt-3 rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
              このセッションのレビュアーとして割り当てられていません
            </div>
          )}

          {canAct && (
            <div className="mt-3 space-y-4">
              <textarea
                placeholder="フィードバックコメントを入力..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={6}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleComplete}
                  disabled={submitting || !comment.trim()}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? '送信中...' : 'フィードバック完了'}
                </button>
                {showSkip && (
                  <button
                    type="button"
                    onClick={handleSkip}
                    disabled={submitting}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    フィードバックしない
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="mt-6">
            <h4 className="text-xs font-semibold text-gray-500">レビュアー状況</h4>
            <div className="mt-2 space-y-1">
              {reviewers.map((r) => (
                <div
                  key={r.reviewer_user_id}
                  className="flex items-center justify-between rounded bg-white p-2 text-sm shadow-sm"
                >
                  <span className="text-gray-700">{r.reviewer_name}</span>
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${
                      r.state === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : r.state === 'skipped'
                          ? 'bg-gray-100 text-gray-500'
                          : r.state === 'in_progress'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-orange-100 text-orange-700'
                    }`}
                  >
                    {r.state === 'completed'
                      ? '完了'
                      : r.state === 'skipped'
                        ? 'スキップ'
                        : r.state === 'in_progress'
                          ? 'レビュー中'
                          : '未着手'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
